import { useState, useEffect } from 'react';

/**
 * Custom hook to check if Google Maps JavaScript API is loaded and ready
 * @param requiredLibrary - Optional library to check for (e.g., 'places', 'marker')
 * @returns boolean indicating if Google Maps is loaded
 */
export const useGoogleMapsLoaded = (requiredLibrary?: 'places' | 'marker'): boolean => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const checkGoogleMaps = () => {
      // Check if google.maps exists
      if (!window.google?.maps) {
        return false;
      }

      // If a specific library is required, check for it
      if (requiredLibrary === 'places') {
        return !!window.google?.maps?.places;
      }

      if (requiredLibrary === 'marker') {
        return !!window.google?.maps?.marker?.AdvancedMarkerElement;
      }

      // Default: just check if maps is loaded
      return true;
    };

    // Initial check
    if (checkGoogleMaps()) {
      setIsLoaded(true);
      return;
    }

    // Poll for Google Maps to load
    const interval = setInterval(() => {
      if (checkGoogleMaps()) {
        setIsLoaded(true);
        clearInterval(interval);
      }
    }, 100);

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, [requiredLibrary]);

  return isLoaded;
};

